import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { GameProvider } from "./context/GameContext";
import MainLayout from "./layouts/MainLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import OneSignal from "react-onesignal";

// Pages
import Home from "./pages/Home";
import Standings from "./pages/Standings";
import Stats from "./pages/Stats";
import News from "./pages/News";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Schedule from "./pages/Schedule";
import ScorerSetup from "./pages/ScorerSetup";
import LiveGame from "./pages/LiveGame";
import AdminDashboard from "./components/admin/AdminDashboard";
import CaptainDashboard from "./pages/CaptainDashboard";
import { ThemeProvider } from "./context/ThemeContext";
import { SeasonProvider } from "./context/SeasonContext";
import { SettingsProvider } from "./context/SettingsContext";
import RegistrationSuccess from "./pages/RegistrationSuccess";
import PublicScoreboard from "./pages/PublicScoreboard";
import TeamProfile from "./pages/TeamProfile";
import GameBoxScore from "./pages/GameBoxScore";
import Clipboard from "./pages/Clipboard";
import Gallery from "./pages/Gallery";
import ScheduleBuilder from "./pages/ScheduleBuilder";
import PlayoffPredictions from "./pages/PlayoffPredictions";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import FreeAgents from "./pages/FreeAgents";
import PlayerDashboard from "./pages/PlayerDashboard";
import PublicPlayerProfile from "./pages/PublicPlayerDashboard";

function App() {
  // ── OneSignal initialisation ────────────────────────────────────────────────
  // Must run inside useEffect so it fires exactly once after mount, never during
  // SSR/pre-render, and never on every re-render (the original bare call did).
  useEffect(() => {
    async function initOneSignal() {
      await OneSignal.init({
        appId: "497f440e-1d05-42ed-9ef2-a746884526ba",

        // Allows testing on http://localhost without HTTPS errors
        allowLocalhostAsSecure: true,

        // Tell OneSignal to use our custom sw.js (which itself imports the
        // OneSignal SDK worker) so our push/click handlers coexist with it.
        serviceWorkerPath: "sw.js",
        serviceWorkerParam: { scope: "/" },

        // Suppress the automatic permission prompt — we ask for permission
        // ourselves inside PlayerSettings via a user-gesture toggle, which
        // satisfies the iOS requirement that permission must be triggered by
        // an explicit user interaction.
        promptOptions: {
          autoPrompt: false,
        },

        // Notify OneSignal when a notification is clicked so it can track
        // open rates in the dashboard.
        notificationClickHandlerMatch: "origin",
        notificationClickHandlerAction: "navigate",
      });
    }

    initOneSignal().catch(console.error);
  }, []); // empty deps → runs once on mount

  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <SeasonProvider>
            <SettingsProvider>
              <GameProvider>
                <Routes>
                  <Route path="/" element={<MainLayout />}>
                    {/* Public Routes */}
                    <Route index element={<Home />} />
                    <Route path="standings" element={<Standings />} />
                    <Route path="stats" element={<Stats />} />
                    <Route path="schedule" element={<Schedule />} />
                    <Route path="news" element={<News />} />
                    <Route path="login" element={<Login />} />
                    <Route
                      path="/forgot-password"
                      element={<ForgotPassword />}
                    />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="register" element={<Register />} />
                    <Route path="/clipboard" element={<Clipboard />} />
                    <Route
                      path="/playoff-predictions"
                      element={<PlayoffPredictions />}
                    />
                    <Route path="/teams/:id" element={<TeamProfile />} />
                    <Route path="/game/:id" element={<GameBoxScore />} />
                    <Route path="gallery" element={<Gallery />} />
                    <Route path="free-agents" element={<FreeAgents />} />
                    <Route
                      path="player-dashboard"
                      element={<PlayerDashboard />}
                    />
                    <Route
                      path="/players/:profileId"
                      element={<PublicPlayerProfile />}
                    />

                    <Route
                      path="gallery/week/:weekNumber"
                      element={<Gallery />}
                    />
                    <Route
                      path="registration-success"
                      element={<RegistrationSuccess />}
                    />
                    <Route path="live" element={<PublicScoreboard />} />
                    {/* Scorekeeper & Admin Routes */}
                    <Route
                      path="scorer"
                      element={
                        <ProtectedRoute allowedRoles={["scorekeeper", "admin"]}>
                          <ScorerSetup />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="scorer/live"
                      element={
                        <ProtectedRoute allowedRoles={["scorekeeper", "admin"]}>
                          <LiveGame />
                        </ProtectedRoute>
                      }
                    />
                    {/* Admin Only Routes */}
                    <Route
                      path="admin"
                      element={
                        <ProtectedRoute allowedRoles={["admin"]}>
                          <AdminDashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="builder"
                      element={
                        <ProtectedRoute allowedRoles={["admin"]}>
                          <ScheduleBuilder />
                        </ProtectedRoute>
                      }
                    />
                    {/* Team Captain Routes — also allow admin so an admin who
                        is also a captain can access their dashboard */}
                    <Route
                      path="captain"
                      element={
                        <ProtectedRoute
                          allowedRoles={["team_captain", "admin"]}
                        >
                          <CaptainDashboard />
                        </ProtectedRoute>
                      }
                    />
                  </Route>
                </Routes>
              </GameProvider>
            </SettingsProvider>
          </SeasonProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
